// The browser's spell check (red underlines) only on the block being edited.
//
// A plain contenteditable checks the whole body, so a document full of code
// identifiers and English file names is covered in red just by reading it.
//
// It relies on the inheritance of the `spellcheck` attribute: the root gets
// `spellcheck="false"` (props.attributes) and the textblock holding the caret
// gets `spellcheck="true"` through a decoration. Leaving the block removes the
// attribute and the browser clears the underlines.
//
// Decorations, not attributes written on ProseMirror's elements: its DOM
// observer rebuilds elements changed from outside and the attribute would vanish.
//
// Nothing while the editor has no focus (the selection stays after blur, which
// would leave one underlined line in a document being read). Focus is plugin
// state; blur dispatches to remove it.
//
// No transaction on focus: at that moment ProseMirror has not read the
// selection the person placed. Dispatching then changes decorations, so
// ProseMirror writes its old selection (the start of the document if none) back
// into the DOM and WebKit scrolls to it - a long press on iPhone jumped to the
// top. Focus is only remembered and the decoration rides on the next
// transaction that changes the selection or the document.

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet, type EditorView } from "@tiptap/pm/view";

/** Plugin state: whether the editor has focus. */
const spellcheckPluginKey = new PluginKey<boolean>("trameriaSpellcheck");

function setFocused(view: EditorView, focused: boolean): void {
  if (spellcheckPluginKey.getState(view.state) === focused) return;
  view.dispatch(view.state.tr.setMeta(spellcheckPluginKey, focused));
}

export function createSpellcheckPlugin(): Plugin<boolean> {
  /** Focus came in but the decoration is not on yet. */
  let pending = false;
  return new Plugin<boolean>({
    key: spellcheckPluginKey,

    state: {
      init: () => false,
      apply(tr, current) {
        const meta = tr.getMeta(spellcheckPluginKey);
        return typeof meta === "boolean" ? meta : current;
      },
    },

    appendTransaction(trs, _old, state) {
      if (!pending) return null;
      if (!trs.some((tr) => tr.selectionSet || tr.docChanged)) return null;
      pending = false;
      if (spellcheckPluginKey.getState(state) === true) return null;
      return state.tr.setMeta(spellcheckPluginKey, true);
    },

    props: {
      attributes: { spellcheck: "false" },

      handleDOMEvents: {
        focus() {
          pending = true;
          return false;
        },
        blur(view) {
          pending = false;
          setFocused(view, false);
          return false;
        },
      },

      decorations(state) {
        if (spellcheckPluginKey.getState(state) !== true) return null;
        const { $from } = state.selection;
        // The textblock holding the caret (paragraph, heading, list item
        // paragraph); nothing at depth 0 or when the parent is not a textblock.
        if ($from.depth === 0 || !$from.parent.isTextblock) return null;
        const start = $from.before();
        const end = $from.after();
        return DecorationSet.create(state.doc, [
          Decoration.node(start, end, { spellcheck: "true" }),
        ]);
      },
    },
  });
}

/** {@link createSpellcheckPlugin} as an extension. */
export const SpellcheckPlugin = Extension.create({
  name: "spellcheckPlugin",

  addProseMirrorPlugins() {
    return [createSpellcheckPlugin()];
  },
});
